/**
 * WithdrawConsent — the operable §7001(c) consent-withdrawal mechanism (CB-5.8, FR-75).
 *
 * Makes the withdrawal right CB-5.3's consent disclosure PROMISED actually work. Available only to a
 * signer who has already consented (and isn't terminal). It is a clearly labeled, honest control —
 * NOT buried, NOT styled as a primary/affirmative trap, NO dark-pattern friction (UX-DR20). The
 * confirmation states the consequences up front: this contract cannot proceed electronically and the
 * sender will be notified to arrange a paper copy. On confirm it calls the token-scoped endpoint,
 * which halts the signer's electronic signing (server-side) and notifies the sender.
 *
 * The exact §7001(c) consequence wording is counsel-gated (CB-5.3's slot, OQ1) — this presents the
 * consequence in the reserved-placeholder voice, not final legal copy.
 */

import { useState } from 'react';
import { Ban } from 'lucide-react';
import { contractService } from '@/services/contractService';
import { accentTextOnLight } from '@/lib/tenantAccent';
import { useDialogA11y } from './useDialogA11y';

interface WithdrawConsentProps {
  token: string;
  companyName: string;
  accent: string;
  /** Called after a successful withdrawal so the shell renders the withdrawn end-state. */
  onWithdrawn: () => void;
}

export default function WithdrawConsent({ token, companyName, accent, onWithdrawn }: WithdrawConsentProps) {
  const [open, setOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // a11y: Escape closes, Tab is trapped within the dialog, focus returns to the trigger on close.
  const dialogRef = useDialogA11y<HTMLDivElement>(open, () => {
    if (!submitting) {
      setOpen(false);
      setError(null);
    }
  });

  async function confirmWithdraw() {
    if (submitting) return;
    setSubmitting(true);
    setError(null);
    const result = await contractService.withdrawConsentV2(token);
    if (result.success) {
      onWithdrawn();
      return;
    }
    setError(result.error || "We couldn't withdraw your consent. Please try again.");
    setSubmitting(false);
  }

  return (
    <>
      {/* Honest, non-buried withdrawal control — secondary to the affirmative flow, never disguised. */}
      <div className="mt-3 text-center">
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="inline-flex min-h-[44px] items-center justify-center gap-1.5 rounded-lg px-3 text-sm font-medium text-slate-500 underline underline-offset-2 hover:text-slate-800 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
        >
          Withdraw electronic consent
        </button>
      </div>

      {open ? (
        <div
          ref={dialogRef}
          tabIndex={-1}
          className="fixed inset-0 z-50 flex items-end justify-center bg-black/40 p-4 sm:items-center focus:outline-none"
          role="dialog"
          aria-modal="true"
          aria-labelledby="withdraw-title"
          aria-describedby="withdraw-consequences"
        >
          <div className="w-full max-w-[480px] rounded-2xl bg-white p-6 shadow-xl">
            <div className="mb-3 flex items-center gap-2">
              <Ban className="h-6 w-6 text-amber-600" aria-hidden="true" />
              <h2 id="withdraw-title" className="text-lg font-semibold text-slate-900">
                Withdraw electronic consent?
              </h2>
            </div>
            <div id="withdraw-consequences" className="space-y-2 text-base leading-relaxed text-slate-600">
              <p>If you withdraw your consent to use electronic records and signatures:</p>
              <ul className="list-disc space-y-1 pl-5 text-sm">
                <li>This contract <strong>cannot be completed electronically</strong> by you.</li>
                <li>
                  {companyName} will be notified, and you can arrange a paper copy with them directly.
                </li>
              </ul>
              <p className="text-xs text-slate-400">
                [Exact §7001(c) consequence wording is finalized before launch.]
              </p>
            </div>
            {error ? (
              <p className="mt-3 text-sm text-red-600" role="alert">
                {error}
              </p>
            ) : null}
            <div className="mt-5 flex flex-col gap-2 sm:flex-row-reverse">
              <button
                type="button"
                onClick={confirmWithdraw}
                disabled={submitting}
                className="flex min-h-[44px] flex-1 items-center justify-center rounded-xl bg-amber-600 px-4 text-base font-semibold text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:opacity-50"
              >
                {submitting ? 'Withdrawing…' : 'Withdraw consent'}
              </button>
              <button
                type="button"
                onClick={() => {
                  setOpen(false);
                  setError(null);
                }}
                disabled={submitting}
                className="flex min-h-[44px] flex-1 items-center justify-center rounded-xl border border-slate-300 px-4 text-base font-semibold text-slate-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
                style={{ color: accentTextOnLight(accent) }}
              >
                Keep my consent
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
}
